import { Alert, Box, Heading, Spinner } from '@hyphen/hyphen-components';

import { useResource } from '../lib/session.js';
import { formatWhen } from './evidence-shared.jsx';

// The column names, in the words the page uses for them. A history that says
// "proposed_drl_t1" is a database talking to itself.
export const FIELD_LABEL = {
  proposed_control: 'Control',
  proposed_drl_t1: 'Documentation request',
  drl_t2: 'Type 2 evidence request',
  population: 'Type 2 population',
  sampling_guidance: 'Type 2 sampling guidance',
  notes: 'Notes',
  status: 'Status',
  tsp_raw: 'Criteria',
  remediation_prose: 'Remediation',
  redapt_note: 'Redapt note',
  task_num: 'Remediation task',
  pof: 'Roadmap entry',
};

export const fieldLabel = (field) => FIELD_LABEL[field] ?? field;

// What each recorded action reads as. Unknown actions show their own name
// rather than nothing: a change nobody has taught this page about still
// happened, and hiding it would make the history a lie.
const ACTION_LABEL = {
  create: 'created this control',
  link_task: 'linked',
  unlink_task: 'unlinked',
  link_roadmap: 'linked',
  unlink_roadmap: 'unlinked',
};

/**
 * Everything recorded about one control, newest first.
 *
 * An examination asks who changed a control and when. The audit log has held
 * the answer since the first migration; this is it, read back.
 */
export function History({ haiID }) {
  const { data: changes, error } = useResource(
    `/controls/${encodeURIComponent(haiID)}/history`, [haiID]);

  if (error) return <Alert variant="warning" title="Could not read the history">{error}</Alert>;
  if (!changes) return <Spinner />;

  return (
    <Box gap="xl">
      <Heading as="h2" size="md">History</Heading>

      {changes.length === 0 && (
        <Box color="secondary">Nothing recorded for this control yet.</Box>
      )}

      <Box gap="lg" maxWidth="48rem">
        {changes.map((change) => <Change key={change.id} change={change} />)}
      </Box>
    </Box>
  );
}

function Change({ change }) {
  return (
    <Box gap="2xs" borderWidth="0 0 0 sm" borderColor="subtle" padding="0 0 0 lg">
      <Box direction="row" gap="sm" alignItems="baseline" fontSize="sm">
        <Box as="span" fontWeight="medium">{change.actor}</Box>
        <Box as="span" color="secondary">{formatWhen(change.at)}</Box>
      </Box>
      <Summary change={change} />
    </Box>
  );
}

function Summary({ change }) {
  if (change.action === 'create') {
    return <Box as="span">Created this control.</Box>;
  }

  if (change.action !== 'update') {
    const what = ACTION_LABEL[change.action] ?? change.action;
    return (
      <Box as="span">
        {what} {fieldLabel(change.field)} <Value value={change.after} />
      </Box>
    );
  }

  return (
    <Box gap="2xs">
      <Box as="span" fontWeight="medium">{fieldLabel(change.field)}</Box>
      <Box gap="2xs" fontSize="sm">
        <Box as="div" color="secondary" style={{ whiteSpace: 'pre-wrap' }}>
          <Value value={change.before} />
        </Box>
        <Box as="div" style={{ whiteSpace: 'pre-wrap' }}>
          <Value value={change.after} />
        </Box>
      </Box>
    </Box>
  );
}

// An absent value shows as a dash rather than as nothing, so "cleared" and
// "set for the first time" both read as a change rather than as half a line.
function Value({ value }) {
  if (value === null || value === undefined || value === '') {
    return <Box as="span" color="tertiary">—</Box>;
  }
  return <>{value}</>;
}
